import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { useBrands } from "../hooks/useBrands";
import { useCategories } from "../hooks/useCategories";
import { useProducts } from "../hooks/useProducts";
import { ProductForm, NO_BRAND_VALUE } from "../components/ProductForm";

export function ProductEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { brands, loading: brandsLoading, error: brandsError } = useBrands();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();
  const { products, loading: productsLoading, error: productsError } = useProducts();

  const product = useMemo(() => {
    if (!id) return null;
    return products.find((p) => p.id === id) ?? null;
  }, [products, id]);

  const loading = brandsLoading || categoriesLoading || productsLoading;
  const error = brandsError || categoriesError || productsError;

  const [didWarnMissingId] = useState(false);
  if (!id) {
    if (!didWarnMissingId) {
      // Avoid React warnings about missing routes during hot reload.
    }
    return <div className="p-6 text-destructive">Missing product id.</div>;
  }

  if (loading || !product) {
    return (
      <div className="p-6">
        <div className="text-muted-foreground">{error ?? "Loading…"}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Edit product</h1>
          <p className="text-muted-foreground">Update product details and status.</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/products">Back</Link>
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Product details</CardTitle>
          <CardDescription>Update name, brand, categories, and status</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-destructive text-sm">{error}</div>
          ) : (
            <ProductForm
              mode="edit"
              productId={id}
              brands={brands}
              categories={categories}
              initialValues={{
                name: product.name,
                sku: product.sku ?? "",
                status: product.status ?? "active",
                brand_id: product.brand_id ? String(product.brand_id) : NO_BRAND_VALUE,
                category_ids: product.category_ids ?? [],
                is_gst_applicable: product.is_gst_applicable ?? false,
                gst_rate: product.gst_rate ?? null,
                gst_type: product.gst_type ?? undefined,
                hsn_code: product.hsn_code ?? "",
              }}
              onSuccess={() => navigate("/products")}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

